/**
 * Video Hero Web Component
 * Handles video playback, lazy-loading, and captions for hero sections
 */
class VideoHero extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  currentVideo = null;
  
  /** @type {number | null} */
  controlsTimeout = null;
  
  /** @type {HTMLElement | null} */
  section = null;
  
  /** @type {HTMLElement | null} */
  container = null;
  
  /** @type {HTMLButtonElement | null} */
  playButton = null;
  
  /** @type {HTMLVideoElement | null} */
  desktopVideo = null;
  
  /** @type {HTMLVideoElement | null} */
  mobileVideo = null;
  
  constructor() {
    super();
    this.isMobile = () => window.innerWidth < 750;
  }

  connectedCallback() {
    // Wait for next tick to ensure DOM is fully parsed
    requestAnimationFrame(() => {
      const section = this.querySelector('[id^="Custom-Video-Hero-"]');
      if (!(section instanceof HTMLElement)) return;
      
      this.section = section;
      this.container = section;
      
      const playButton = this.querySelector('.custom-video-hero__play-button');
      this.playButton = playButton instanceof HTMLButtonElement ? playButton : null;
      
      const desktopVideo = this.querySelector('.custom-video-hero__video--desktop');
      this.desktopVideo = desktopVideo instanceof HTMLVideoElement ? desktopVideo : null;
      
      const mobileVideo = this.querySelector('.custom-video-hero__video--mobile');
      this.mobileVideo = mobileVideo instanceof HTMLVideoElement ? mobileVideo : null;
      
      this.setupLazyLoading();
      this.init();
    });
  }

  /**
   * Sets up lazy-loading for videos using Intersection Observer
   */
  setupLazyLoading() {
    // Use Intersection Observer for lazy-loading videos
    /** @type {HTMLVideoElement[]} */
    const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);
    
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target instanceof HTMLVideoElement) {
            const video = entry.target;
            // Load the video when it's about to enter viewport
            if (video.readyState === 0) {
              video.load();
              
              // If data-autoplay is set, play the video when it loads
              if (video.dataset.autoplay === 'true') {
                video.addEventListener('loadeddata', () => {
                  video.play().catch((/** @type {Error} */ err) => {
                    console.warn('Autoplay failed:', err);
                  });
                }, { once: true });
              }
            }
            observer.unobserve(video);
          }
        });
      }, {
        rootMargin: '50px' // Start loading 50px before entering viewport
      });

      videos.forEach(video => {
        observer.observe(video);
      });
    } else {
      // Fallback: load immediately if no IntersectionObserver support
      videos.forEach(video => {
        video.load();
        
        // If data-autoplay is set, play the video when it loads
        if (video.dataset.autoplay === 'true') {
          video.addEventListener('loadeddata', () => {
            video.play().catch((/** @type {Error} */ err) => {
              console.warn('Autoplay failed:', err);
            });
          }, { once: true });
        }
      });
    }
  }

  disconnectedCallback() {
    // Cleanup when element is removed from DOM
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
    
    // Revoke Blob URLs to prevent memory leaks
    /** @type {HTMLVideoElement[]} */
    const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);
    videos.forEach(video => {
      const blobUrls = video.dataset.captionBlobUrls;
      if (blobUrls) {
        blobUrls.split(',').forEach(url => {
          URL.revokeObjectURL(url.trim());
        });
      }
    });
  }

  init() {
    this.setCurrentVideo();
    this.setupCaptions();
    this.setupNativeControls();
    this.removeInitialLoadClass();
    this.setupEventListeners();
    this.pauseInactiveVideo();
  }

  /**
   * Sets up captions by creating Blob URLs from VTT content in data attributes
   */
  setupCaptions() {
    /** @type {HTMLVideoElement[]} */
    const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);
    
    videos.forEach(video => {
      const captionsEnabled = video.dataset.captionsEnabled === 'true';
      if (!captionsEnabled) return;
      
      const captionsDefault = video.dataset.captionsDefault === 'true';
      
      // Language codes to check
      const languages = [
        { code: 'en', label: 'English' },
        { code: 'es', label: 'Español' },
        { code: 'fr', label: 'Français' },
        { code: 'de', label: 'Deutsch' },
        { code: 'it', label: 'Italiano' },
        { code: 'pt', label: 'Português' },
        { code: 'nl', label: 'Nederlands' }
      ];
      
      let isFirstTrack = true;
      
      languages.forEach(lang => {
        const vttContent = video.dataset[`caption${lang.code.charAt(0).toUpperCase()}${lang.code.slice(1)}`];
        
        if (vttContent) {
          // Create Blob URL from VTT content
          const blob = new Blob([vttContent], { type: 'text/vtt' });
          const blobUrl = URL.createObjectURL(blob);
          
          // Create track element
          const track = document.createElement('track');
          track.kind = 'captions';
          track.src = blobUrl;
          track.srclang = lang.code;
          track.label = lang.label;
          
          // Set first track as default if captions_default_on is true
          if (isFirstTrack && captionsDefault) {
            track.default = true;
            isFirstTrack = false;
          }
          
          // Add track to video
          video.appendChild(track);
          
          // Store blob URL for cleanup
          if (!video.dataset.captionBlobUrls) {
            video.dataset.captionBlobUrls = blobUrl;
          } else {
            video.dataset.captionBlobUrls += ',' + blobUrl;
          }
        }
      });
    });
  }

  /**
   * Removes initial load class after first play
   */
  removeInitialLoadClass() {
    // Remove initial-load class after first play on autoplay
    if (this.container?.classList.contains('custom-video-hero--initial-load')) {
      /** @type {HTMLVideoElement[]} */
      const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);
      videos.forEach(video => {
        video.addEventListener('play', () => {
          this.container?.classList.remove('custom-video-hero--initial-load');
        }, { once: true });
      });
    }
  }

  /**
   * Sets up native video controls
   */
  setupNativeControls() {
    // Check if native controls should be enabled
    const enableNativeControls = this.section?.dataset.nativeControls === 'true';
    
    /** @type {HTMLVideoElement[]} */
    const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);
    videos.forEach(video => {
      // Always remove download button from native controls
      video.setAttribute('controlsList', 'nodownload');
      // Keep Picture-in-Picture for better UX
      video.disablePictureInPicture = false;
      
      // Enable native controls on first play if setting is enabled
      if (enableNativeControls) {
        video.addEventListener('play', () => {
          video.controls = true;
        }, { once: true }); // Only fire once
      }
    });
  }

  setCurrentVideo() {
    // Determine which video is visible based on screen size
    const isMobile = window.innerWidth < 750;
    this.currentVideo = isMobile && this.mobileVideo ? this.mobileVideo : this.desktopVideo;
  }

  pauseInactiveVideo() {
    // Pause the video that is not currently visible
    const inactiveVideo = this.currentVideo === this.desktopVideo ? this.mobileVideo : this.desktopVideo;
    if (inactiveVideo && !inactiveVideo.paused) {
      inactiveVideo.pause();
    }
  }

  showControlsTemporarily() {
    // Only on mobile
    if (!this.isMobile()) return;

    // Show controls
    this.container?.classList.add('custom-video-hero--show-controls');

    // Clear existing timeout
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }

    // Hide controls after 4 seconds
    this.controlsTimeout = setTimeout(() => {
      this.container?.classList.remove('custom-video-hero--show-controls');
    }, 1500);
  }

  hideControls() {
    // Only on mobile
    if (!this.isMobile()) return;
    
    this.container?.classList.remove('custom-video-hero--show-controls');
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
  }

  /**
   * Sets up event listeners for video controls
   */
  setupEventListeners() {
    // Play/Pause button click
    if (this.playButton) {
      this.playButton.addEventListener('click', () => {
        this.togglePlay();
        
        // Remove focus from button after click (especially for mobile)
        if (this.isMobile()) {
          this.playButton?.blur();
          // Also hide controls immediately after action
          setTimeout(() => this.hideControls(), 300);
        }
      });
    }

    // Setup event listeners for both videos
    /** @type {HTMLVideoElement[]} */
    const videos = [this.desktopVideo, this.mobileVideo].filter((v) => v instanceof HTMLVideoElement);

    // Click/Touch on video to toggle play/pause (desktop and mobile)
    videos.forEach(video => {
      /**
       * Handles video click/touch events
       * @param {Event} e - The event object
       */
      const handleVideoClick = (e) => {
        if (video === this.currentVideo) {
          e.preventDefault();
          e.stopPropagation();
          
          // Toggle play/pause
          this.togglePlay();
          
          // If video is now playing, show controls temporarily (mobile only)
          if (this.isMobile()) {
            setTimeout(() => {
              if (this.currentVideo && !this.currentVideo.paused) {
                this.showControlsTemporarily();
              }
            }, 100);
          }
        }
      };
      
      // Add both click and touch events
      video.addEventListener('click', handleVideoClick);
      video.addEventListener('touchend', handleVideoClick);
    });
    
    videos.forEach(video => {
      video.addEventListener('play', () => {
        if (this.container) {
          this.container.classList.add('custom-video-hero--playing');
        }
        if (this.playButton) {
          this.playButton.setAttribute('aria-label', 'Pause video');
        }
      });

      video.addEventListener('pause', () => {
        if (this.container) {
          this.container.classList.remove('custom-video-hero--playing');
        }
        if (this.playButton) {
          this.playButton.setAttribute('aria-label', 'Play video');
        }
      });

      // Handle autoplay that might have already started
      video.addEventListener('playing', () => {
        if (this.container) {
          this.container.classList.add('custom-video-hero--playing');
        }
        if (this.playButton) {
          this.playButton.setAttribute('aria-label', 'Pause video');
        }
      });

      // Check initial state when video metadata loads
      video.addEventListener('loadeddata', () => {
        if (!video.paused && video === this.currentVideo) {
          if (this.container) {
            this.container.classList.add('custom-video-hero--playing');
          }
          if (this.playButton) {
            this.playButton.setAttribute('aria-label', 'Pause video');
          }
        }
      });
    });

    // Update current video on resize
    window.addEventListener('resize', () => {
      const wasPaused = this.currentVideo?.paused;
      this.setCurrentVideo();
      this.pauseInactiveVideo();
      
      // Sync state to new video
      if (this.currentVideo && !wasPaused) {
        this.currentVideo.play();
      }
    });
  }

  togglePlay() {
    if (!this.currentVideo) return;

    if (this.currentVideo.paused) {
      // Pause the other video before playing current
      const otherVideo = this.currentVideo === this.desktopVideo ? this.mobileVideo : this.desktopVideo;
      if (otherVideo) {
        otherVideo.pause();
      }
      
      this.currentVideo.play().catch((/** @type {Error} */ err) => {
        console.warn('Video play failed:', err);
      });
    } else {
      this.currentVideo.pause();
    }
  }
}

// Register the Web Component
if (!customElements.get('video-hero')) {
  customElements.define('video-hero', VideoHero);
}
